import { createContext, useContext, useState, useEffect, useCallback, ReactNode } from "react";
import { useAuth } from "@/hooks/useAuth";
import { supabase } from "@/integrations/supabase/client";

export interface ProPlan {
  id: "monthly" | "annual" | "lifetime";
  name: string;
  price: string;
  period: string;
  priceValue: number;
  savings?: string;
  badge?: string;
  priceId: string;
  productId: string;
}

export const PRO_PLANS: ProPlan[] = [
  {
    id: "monthly",
    name: "Pro Monthly",
    price: "$6.99",
    period: "/month",
    priceValue: 6.99,
    priceId: "price_1TGsR49Pqt1ROal1hl6IpqYX",
    productId: "prod_UFNBfVKw3hXnyl",
  },
  {
    id: "annual",
    name: "Pro Annual",
    price: "$49.99",
    period: "/year",
    priceValue: 49.99,
    savings: "Save 40%",
    badge: "Best Value",
    priceId: "price_1TGsV79Pqt1ROal1lVutzYGn",
    productId: "prod_UFNFYvdrWhVpYn",
  },
  {
    id: "lifetime",
    name: "Founder Lifetime",
    price: "$39.99",
    period: "one-time",
    priceValue: 39.99,
    savings: "Limited to first 160 users",
    badge: "🔥 Launch Special",
    priceId: "price_1TGsWL9Pqt1ROal1AyBD6k4c",
    productId: "prod_UFNG6CAlp0WByN",
  },
];

export type ProFeature =
  | "unlimited_ai_chat"
  | "push_alerts"
  | "leave_by_timer"
  | "saved_airports"
  | "security_forecast"
  | "hourly_chart";

const PRO_FEATURE_LABELS: Record<ProFeature, string> = {
  unlimited_ai_chat: "Unlimited AI Chat",
  push_alerts: "Push Notification Alerts",
  leave_by_timer: "Leave-By Timer",
  saved_airports: "Saved Airports",
  security_forecast: "Security Line Forecast",
  hourly_chart: "Hourly Wait Chart",
};

interface ProContextType {
  isPro: boolean;
  isProLoading: boolean;
  proTier: string | null;
  subscriptionEnd: string | null;
  checkFeature: (feature: ProFeature) => boolean;
  getFeatureLabel: (feature: ProFeature) => string;
  refreshProStatus: () => Promise<void>;
}

const ProContext = createContext<ProContextType>({
  isPro: false,
  isProLoading: true,
  proTier: null,
  subscriptionEnd: null,
  checkFeature: () => false,
  getFeatureLabel: (f) => PRO_FEATURE_LABELS[f],
  refreshProStatus: async () => {},
});

export const useProStatus = () => useContext(ProContext);

export const ProProvider = ({ children }: { children: ReactNode }) => {
  const { user, session } = useAuth();
  const [isPro, setIsPro] = useState(false);
  const [isProLoading, setIsProLoading] = useState(true);
  const [proTier, setProTier] = useState<string | null>(null);
  const [subscriptionEnd, setSubscriptionEnd] = useState<string | null>(null);

  const refreshProStatus = useCallback(async () => {
    if (!session?.access_token) {
      setIsPro(false);
      setProTier(null);
      setSubscriptionEnd(null);
      setIsProLoading(false);
      return;
    }

    try {
      const { data, error } = await supabase.functions.invoke("check-subscription", {
        headers: { Authorization: `Bearer ${session.access_token}` },
      });

      if (error) {
        console.error("check-subscription error:", error);
        setIsPro(false);
      } else if (data?.subscribed) {
        setIsPro(true);
        setProTier(data.tier || null);
        setSubscriptionEnd(data.subscription_end || null);
      } else {
        setIsPro(false);
        setProTier(null);
        setSubscriptionEnd(null);
      }
    } catch (err) {
      console.error("Failed to check subscription:", err);
      setIsPro(false);
    } finally {
      setIsProLoading(false);
    }
  }, [session?.access_token]);

  // When the user signs in, attempt to claim any anonymous Apple purchase
  // that was made on this device before they had an account.
  // (Apple Guideline 5.1.1(v) — purchase first, optional sign-in after.)
  useEffect(() => {
    const claimPending = async () => {
      if (!session?.access_token) return;
      let pendingOtid: string | null = null;
      try {
        pendingOtid = localStorage.getItem("ab_pending_apple_otid");
      } catch {
        return;
      }
      if (!pendingOtid) return;

      try {
        const { data, error } = await supabase.functions.invoke("claim-apple-purchase", {
          body: { originalTransactionId: pendingOtid },
          headers: { Authorization: `Bearer ${session.access_token}` },
        });
        if (!error && data?.claimed) {
          try { localStorage.removeItem("ab_pending_apple_otid"); } catch { /* ignore */ }
          await refreshProStatus();
        } else if (data?.reason === "already_claimed" || data?.reason === "not_found") {
          // Stop retrying — the receipt is gone or owned by someone else.
          try { localStorage.removeItem("ab_pending_apple_otid"); } catch { /* ignore */ }
        }
      } catch (err) {
        console.error("[Pro] Failed to claim pending Apple purchase:", err);
      }
    };
    claimPending();
  }, [session?.access_token, refreshProStatus]);

  useEffect(() => {
    refreshProStatus();
  }, [refreshProStatus]);

  // Auto-refresh every 60 seconds
  useEffect(() => {
    if (!session?.access_token) return;
    const interval = setInterval(refreshProStatus, 60_000);
    return () => clearInterval(interval);
  }, [session?.access_token, refreshProStatus]);

  const checkFeature = (feature: ProFeature): boolean => isPro;

  const getFeatureLabel = (feature: ProFeature): string => PRO_FEATURE_LABELS[feature];

  return (
    <ProContext.Provider value={{ isPro, isProLoading, proTier, subscriptionEnd, checkFeature, getFeatureLabel, refreshProStatus }}>
      {children}
    </ProContext.Provider>
  );
};
